import { useCallback, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { getTokenExpiry } from '../utils/tokenStorage';

type UseTokenSyncProps = {
  storedExpiryTimestamp: number | null;
  onTokenChange: (expiry: number) => void;
  onTokenExpired: () => void;
};

/**
 * Hook to handle cross-tab token synchronization for web platforms
 */
export const useTokenSync = ({
  storedExpiryTimestamp,
  onTokenChange,
  onTokenExpired,
}: UseTokenSyncProps): void => {
  const handleVisibilityChange = useCallback(async (): Promise<void> => {
    if (document.visibilityState === 'visible') {
      // Check if expiry has changed in another tab
      const storedExpiry = await getTokenExpiry();
      if (storedExpiry != null && storedExpiry !== storedExpiryTimestamp) {
        const now = Date.now();
        if (storedExpiry > now) {
          // Restart tracking with updated expiry
          onTokenChange(storedExpiry);
        } else {
          // Token expired in another tab
          onTokenExpired();
        }
      }
    }
  }, [storedExpiryTimestamp, onTokenChange, onTokenExpired]);

  // Only enable for web platform
  const isWebPlatform = useMemo(() => Platform.OS === 'web', []);

  useEffect(() => {
    if (!isWebPlatform) return;

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isWebPlatform, handleVisibilityChange]);
};
